import { useState, useEffect } from 'react'
import { Switch } from '@headlessui/react'
import ApiClient from '../../utils/ApiClient'

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

export default function EmailNotificationSettings() {
    const [settings, setSettings] = useState({
        enabled: true,
        recipients: [],
        batchProcessNotifications: true
    })
    const [newRecipient, setNewRecipient] = useState('')
    const [isLoading, setIsLoading] = useState(true)
    const [isSaving, setIsSaving] = useState(false)
    const [isTesting, setIsTesting] = useState(false)
    const [testEmail, setTestEmail] = useState('')
    const [saveMessage, setSaveMessage] = useState('')

    useEffect(() => {
        loadSettings()
    }, [])

    const loadSettings = async () => {
        try {
            setIsLoading(true)
            const response = await ApiClient.get('/api/user/email-settings')
            setSettings(response.data)
        } catch (error) {
            console.error('Failed to load email settings:', error)
        } finally {
            setIsLoading(false)
        }
    }

    const saveSettings = async () => {
        try {
            setIsSaving(true)
            setSaveMessage('')
            await ApiClient.post('/api/user/email-settings', settings)
            setSaveMessage('Settings saved successfully!')
            setTimeout(() => setSaveMessage(''), 3000)
        } catch (error) {
            console.error('Failed to save email settings:', error)
            setSaveMessage('Failed to save settings. Please try again.')
            setTimeout(() => setSaveMessage(''), 5000)
        } finally {
            setIsSaving(false)
        }
    }

    const addRecipient = async () => {
        if (newRecipient && !settings.recipients.includes(newRecipient)) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            if (!emailRegex.test(newRecipient)) {
                alert('Please enter a valid email address')
                return
            }
            
            // Update local state first (immediate UI feedback)
            const newSettings = {
                ...settings,
                recipients: [...settings.recipients, newRecipient]
            }
            setSettings(newSettings)
            setNewRecipient('')
            
            // Auto-save to backend
            try {
                await ApiClient.post('/api/user/email-settings', newSettings)
                console.log('Email recipient saved successfully')
            } catch (error) {
                console.error('Failed to save email recipient:', error)
                // Revert local state on error
                setSettings(settings)
                setNewRecipient(newRecipient) // restore input value
                alert('Failed to save email recipient. Please try again.')
            }
        }
    }

    const removeRecipient = async (email) => {
        // Store current state for potential rollback
        const currentSettings = settings
        
        // Update local state first (immediate UI feedback)
        const newSettings = {
            ...settings,
            recipients: settings.recipients.filter(r => r !== email)
        }
        setSettings(newSettings)
        
        // Auto-save to backend
        try {
            await ApiClient.post('/api/user/email-settings', newSettings)
            console.log('Email recipient removed successfully')
        } catch (error) {
            console.error('Failed to remove email recipient:', error)
            // Revert local state on error
            setSettings(currentSettings)
            alert('Failed to remove email recipient. Please try again.')
        }
    }

    const sendTestEmail = async () => {
        if (!testEmail) {
            alert('Please enter an email address for the test')
            return
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(testEmail)) {
            alert('Please enter a valid email address')
            return
        }

        try {
            setIsTesting(true)
            await ApiClient.post('/api/user/email-settings/test', { email: testEmail })
            alert('Test email sent successfully!')
            setTestEmail('')
        } catch (error) {
            console.error('Failed to send test email:', error)
            alert('Failed to send test email. Please try again.')
        } finally {
            setIsTesting(false)
        }
    }

    if (isLoading) {
        return (
            <div className="py-6 sm:flex">
                <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">Email Notifications</dt>
                <dd className="mt-1 sm:mt-0 sm:flex-auto">
                    <div className="animate-pulse">
                        <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
                        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    </div>
                </dd>
            </div>
        )
    }

    return (
        <div className="py-6 sm:flex">
            <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">Email Notifications</dt>
            <dd className="mt-1 sm:mt-0 sm:flex-auto">
                <div className="space-y-6">
                    {/* Enable/Disable Switch */}
                    <div className="flex items-center justify-between">
                        <div>
                            <span className="text-sm font-medium text-gray-900">Enable Notifications</span>
                            <p className="text-sm text-gray-500">Receive email notifications for batch processes</p>
                        </div>
                        <Switch
                            checked={settings.enabled}
                            onChange={(enabled) => setSettings(prev => ({ ...prev, enabled }))}
                            className={classNames(
                                settings.enabled ? 'bg-indigo-600' : 'bg-gray-200',
                                'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2'
                            )}
                        >
                            <span
                                aria-hidden="true"
                                className={classNames(
                                    settings.enabled ? 'translate-x-5' : 'translate-x-0',
                                    'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                                )}
                            />
                        </Switch>
                    </div>

                    {settings.enabled && (
                        <>
                            {/* Batch Process Notifications Switch */}
                            <div className="flex items-center justify-between">
                                <div>
                                    <span className="text-sm font-medium text-gray-900">Batch Process Notifications</span>
                                    <p className="text-sm text-gray-500">Get notified when batch processes complete</p>
                                </div>
                                <Switch
                                    checked={settings.batchProcessNotifications}
                                    onChange={(batchProcessNotifications) => setSettings(prev => ({ ...prev, batchProcessNotifications }))}
                                    className={classNames(
                                        settings.batchProcessNotifications ? 'bg-indigo-600' : 'bg-gray-200',
                                        'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2'
                                    )}
                                >
                                    <span
                                        aria-hidden="true"
                                        className={classNames(
                                            settings.batchProcessNotifications ? 'translate-x-5' : 'translate-x-0',
                                            'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                                        )}
                                    />
                                </Switch>
                            </div>

                            {settings.batchProcessNotifications && (
                                <>
                                    {/* Recipients Section */}
                                    <div>
                                        <h4 className="text-sm font-medium text-gray-900 mb-3">Email Recipients</h4>
                                        
                                        {/* Add Recipient */}
                                        <div className="flex gap-2 mb-3">
                                            <input
                                                type="email"
                                                value={newRecipient}
                                                onChange={(e) => setNewRecipient(e.target.value)}
                                                placeholder="Enter email address"
                                                className="flex-1 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                                                onKeyPress={(e) => e.key === 'Enter' && addRecipient()}
                                            />
                                            <button
                                                type="button"
                                                onClick={addRecipient}
                                                className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                            >
                                                Add
                                            </button>
                                        </div>

                                        {/* Recipients List */}
                                        {settings.recipients.length > 0 ? (
                                            <div className="space-y-2">
                                                {settings.recipients.map((email) => (
                                                    <div key={email} className="flex items-center justify-between bg-indigo-50 border border-indigo-200 px-4 py-3 rounded-lg shadow-sm">
                                                        <span className="text-sm text-gray-900">{email}</span>
                                                        <button
                                                            type="button"
                                                            onClick={() => removeRecipient(email)}
                                                            className="text-red-600 hover:text-red-500 text-sm font-medium"
                                                        >
                                                            Remove
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-sm text-gray-500 italic">No recipients configured</p>
                                        )}
                                    </div>

                                    {/* Test Email Section */}
                                    <div>
                                        <h4 className="text-sm font-medium text-gray-900 mb-3">Test Email</h4>
                                        <div className="flex gap-2">
                                            <input
                                                type="email"
                                                value={testEmail}
                                                onChange={(e) => setTestEmail(e.target.value)}
                                                placeholder="Enter email to test"
                                                className="flex-1 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                                            />
                                            <button
                                                type="button"
                                                onClick={sendTestEmail}
                                                disabled={isTesting}
                                                className="rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {isTesting ? 'Sending...' : 'Send Test'}
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </>
                    )}

                    {/* Save Button and Status */}
                    <div className="flex items-center gap-4 pt-4 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={saveSettings}
                            disabled={isSaving}
                            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isSaving ? 'Saving...' : 'Save Settings'}
                        </button>
                        {saveMessage && (
                            <span className={classNames(
                                saveMessage.includes('success') ? 'text-green-600' : 'text-red-600',
                                'text-sm font-medium'
                            )}>
                                {saveMessage}
                            </span>
                        )}
                    </div>
                </div>
            </dd>
        </div>
    )
}